import React, { useState } from 'react'
import { Booklet, Team } from '../../../../types/generated.types'
import { IoChevronBackSharp, IoChevronForwardSharp } from 'react-icons/io5'
import Select from 'react-select'
import { useQuery } from '@apollo/client'
import { statOrdersByTeamQuery } from '../../../../queries/team'
import { getBookletCompaniesCountQuery } from '../../../../queries/booklet'
import CountUp from 'react-countup'
import BookletDetails from '../../BookletDetails'

type TeamSalesProps = {
  team: Team
  associationBooklets: Booklet[]
  selectedBooklet?: Booklet
  onBookletChange: (booklet: Booklet | undefined) => void
}

const TeamSales: React.FC<TeamSalesProps> = ({ team, associationBooklets, selectedBooklet, onBookletChange }) => {
  // Create options for react-select
  const bookletOptions = associationBooklets
    .filter((booklet: Booklet) => booklet.validFrom)
    .sort((a, b) => new Date(b.validFrom!).getTime() - new Date(a.validFrom!).getTime())
    .map((booklet: Booklet) => ({
      value: booklet,
      label: booklet.title
    }));

  // Query for team sales statistics filtered by booklet
  const { data: statsOrderTeams } = useQuery(statOrdersByTeamQuery, {
    variables: {
      teamId: team?._id,
      bookletId: selectedBooklet?._id || null,
      fromDate: null,
      toDate: null
    },
    skip: !selectedBooklet
  });

  // Query for booklet companies and coupons count
  const { data: countData } = useQuery(getBookletCompaniesCountQuery, {
    variables: { _id: selectedBooklet?._id },
    skip: !selectedBooklet
  });

  // Hämta antal sålda häften och förtjänst från backend-statistik
  const webshopOrdersCount = statsOrderTeams?.orders?.webshopOrders?.length || 0;
  const webshopEarnings = statsOrderTeams?.orders?.webshopEarnings || 0;

  // Räkna antal butiker och kuponger
  const companyCount = countData?.booklet?.calculatedCompanies?.length || 0;
  const couponCount = countData?.booklet?.calculatedCompanies
    ? countData.booklet.calculatedCompanies.reduce((sum: number, company: any) => sum + (company.calculatedCoupons?.length || 0), 0)
    : 0;

  // Visa köphistorik från webshopOrders
  const purchaseHistory = statsOrderTeams?.orders?.webshopOrders?.map((order: any) => ({
    date: new Date(order.completedDate).toLocaleDateString('sv-SE'),
    quantity: '1 häfte',
    price: `${order.totalPrice} kr`
  })) || [];

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5
  const totalPages = Math.ceil(purchaseHistory.length / itemsPerPage)

  // Only show pagination if more than one page
  const showPagination = totalPages > 1

  // Get current page data
  const currentData = purchaseHistory.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const handlePreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const handleBookletChange = (selectedOption: any) => onBookletChange(selectedOption.value);


  return (
    <div className="container flex justify-center mb">
      <div className="box md">
        <div className="booklet-selector">
          <h3>Försäljning</h3>
          <div className="select-container">
            <Select
              value={bookletOptions.find(option => option.value === selectedBooklet)}
              onChange={handleBookletChange}
              options={bookletOptions}
              placeholder="Välj häfte"
              isSearchable={false}
              classNamePrefix="select"
            />
          </div>
        </div>

        <div className="mb">
          <BookletDetails
            booklet={selectedBooklet}
            companyCount={companyCount}
            couponCount={couponCount}
          />
        </div>

        <div className="sales-stats">
          <div className="stat-box">
            <div className="stat-label">Förtjänst</div>
            <div className="stat-value">
              {/* @ts-ignore */}
              <CountUp end={webshopEarnings} />
              <span>kr</span>
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-label">Sålda häften</div>
            <div className="stat-value">
              {/* @ts-ignore */}
              <CountUp end={webshopOrdersCount} />
              <span>st</span>
            </div>
          </div>
        </div>

        <b className='mt block'>Köphistorik</b>
        <div className="purchase-history">
          {purchaseHistory.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Antal</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.date}</td>
                    <td>{item.quantity}</td>
                    <td>{item.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="no-sales">Inga köp än. Gå till materialfliken för att ladda hem material och komma igång med försäljningen.</p>
          )}
        </div>

        {showPagination && (
          <div className="pagination">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              <IoChevronBackSharp />
            </button>
            <span className="pagination-info">
              Sida {currentPage} av {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              <IoChevronForwardSharp />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default TeamSales
